import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { identifyPostHog, trackEvent } from "@/lib/posthog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ShieldCheck, KeyRound } from "lucide-react";
import { PlatformWordmark, PlatformIcon } from "@/components/brand/PlatformWordmark";

const DEFAULT_BRAND = {
  name: "AIFLedger",
  tagline: "Institutional Fund Administration",
  primary_color: "#0b192c",
  accent_color: "#c4a454",
  logo_url: "/corpzo-icon-sm.png",
  is_root: true,
};

function formatErr(d) {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function Login() {
  const { login, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  // MFA state
  const [mfaStage, setMfaStage] = useState(false);   // true → show code prompt
  const [mfaName, setMfaName] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  // Practice-branded login: if we're on `{slug}.aifledger.com` (or a query
  // ?practice=slug for previews), fetch the tenant's public branding and swap
  // the logo/name. Falls back to Corpzo defaults when no slug is detected.
  React.useEffect(() => {
    let slug = "";
    try {
      const host = window.location.hostname || "";
      const q = new URLSearchParams(window.location.search).get("practice");
      const parts = host.split(".");
      // e.g. motilal.aifledger.com → ["motilal","aifledger","com"]
      if (parts.length >= 3 && ["aifledger", "corpzo"].some((k) => host.includes(k))) {
        const first = parts[0].toLowerCase();
        // Reserved subdomains that are never a tenant slug.
        const reserved = ["www", "app", "auth", "api", "cdn", "admin",
                          "preview", "fundops-india"];
        if (first && !reserved.includes(first)) {
          slug = first;
        }
      }
      if (q) slug = q.trim().toLowerCase();
    } catch { /* ignore */ }
    if (!slug) return;
    api.get(`/public/branding?subdomain=${encodeURIComponent(slug)}`)
      .then(({ data }) => { if (data && !data.is_root) setBrand({ ...DEFAULT_BRAND, ...data }); })
      .catch(() => { /* stay on default */ });
  }, []);

  const landingFor = (u) => {
    if (!u) return "/dashboard";
    if (u.active_role === "investor") return "/investor";
    if (u.active_role === "distributor") return "/distributor";
    return "/dashboard";
  };

  React.useEffect(() => {
    if (user) navigate(landingFor(user));
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password, rememberMe);
      if (data?.mfa_required) {
        setMfaStage(true);
        setMfaName(data.name || "");
        toast.info("Enter the 6-digit code from your authenticator");
      } else {
        toast.success(`Welcome, ${data.name}`);
        navigate(landingFor(data));
      }
    } catch (err) {
      trackEvent("login_failed", {
        reason: err?.response?.status === 429 ? "locked" : "invalid_credentials",
        status: err?.response?.status || 0,
      });
      toast.error(formatErr(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    if (!useRecovery && code.length !== 6) return toast.error("Enter the 6-digit code");
    if (useRecovery && code.length < 8) return toast.error("Enter your recovery code");
    setLoading(true);
    try {
      const endpoint = useRecovery ? "/mfa/verify-recovery" : "/mfa/verify-login";
      const body = useRecovery
        ? { email, recovery_code: code, remember_me: rememberMe }
        : { email, code, remember_me: rememberMe };
      const { data } = await api.post(endpoint, body);
      setUser(data);
      identifyPostHog(data);
      trackEvent("mfa_verify_succeeded", {
        active_role: data.active_role,
        via: useRecovery ? "recovery_code" : "totp",
      });
      trackEvent("login_succeeded", {
        active_role: data.active_role,
        via: "mfa",
        roles_count: (data.roles || []).length,
      });
      if (useRecovery && typeof data.mfa_recovery_codes_remaining === "number") {
        toast.success(`Signed in via recovery code — ${data.mfa_recovery_codes_remaining} of 8 codes remain. Rotate them soon.`);
      } else {
        toast.success(`Welcome, ${data.name}`);
      }
      navigate(landingFor(data));
    } catch (err) {
      trackEvent("mfa_verify_failed", { status: err?.response?.status || 0, via: useRecovery ? "recovery_code" : "totp" });
      toast.error(formatErr(err?.response?.data?.detail) || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const demoAccounts = [];  // demo panel removed for production
  return (
    <div className="min-h-screen flex bg-[#fdfcf8]" data-testid="login-page">
      {/* Left panel — brand image */}
      <div className="hidden lg:flex flex-col login-overlay flex-1 p-12 text-white relative">
        <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity cursor-pointer"
              data-testid="login-brand" aria-label="Back to home">
          {brand.is_root ? (
            <>
              <PlatformIcon className="h-11 w-11 rounded bg-white/5 p-1" width={44} height={44} />
              <div>
                <div className="bg-white rounded px-2.5 py-1.5 inline-block">
                  <PlatformWordmark className="h-9" data-testid="login-brand-name" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-2 font-semibold">
                  Institutional Fund Administration
                </div>
              </div>
            </>
          ) : (
            <>
              <img src={brand.logo_url || "/corpzo-icon-sm.png"} alt={brand.name} className="h-11 w-11 rounded" />
              <div>
                <div className="font-serif text-3xl tracking-tight leading-none" data-testid="login-brand-name">
                  {brand.name}<span style={{ color: brand.accent_color || "#c4a454" }}>.</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1.5 font-semibold">
                  {brand.tagline || "Fund Administration"}
                </div>
              </div>
            </>
          )}
        </Link>
        <div className="mt-auto max-w-md">
          <h1 className="font-serif text-5xl leading-tight tracking-tight">
            Institutional-grade<br />
            <span style={{ color: "#c4a454" }}>fund administration,</span><br />
            built for India.
          </h1>
          <p className="mt-6 text-white/75 text-sm leading-relaxed">
            Fund accounting, investor onboarding, capital calls, NAV, compliance and SEBI-aligned reporting —
            for Category I, II and III AIFs.
          </p>
          <div className="mt-8 flex items-center gap-6 text-xs text-white/60">
            <div className="flex items-center gap-2"><ShieldCheck size={14} style={{ color: "#c4a454" }} /> SEBI-aligned</div>
            <div className="flex items-center gap-2"><Lock size={14} style={{ color: "#c4a454" }} /> Maker-checker audit</div>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8" data-testid="login-brand-mobile">
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity cursor-pointer"
                  aria-label="Back to home">
              {brand.is_root ? (
                <>
                  <PlatformIcon className="h-10 w-10 rounded" width={40} height={40} />
                  <div>
                    <PlatformWordmark className="h-6" data-testid="login-brand-name-mobile" />
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#475569] mt-1 font-semibold">
                      Institutional Fund Administration
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <img src={brand.logo_url || "/corpzo-icon-sm.png"} alt={brand.name} className="h-10 w-10 rounded" />
                  <div>
                    <div className="font-serif text-2xl tracking-tight leading-none text-[#0b192c]" data-testid="login-brand-name-mobile">
                      {brand.name}<span style={{ color: brand.accent_color || "#c4a454" }}>.</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#475569] mt-1 font-semibold">
                      {brand.tagline || "Fund Administration"}
                    </div>
                  </div>
                </>
              )}
            </Link>
          </div>

          {!mfaStage ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] font-semibold">Sign in</div>
              <h2 className="font-serif text-4xl text-[#0b192c] mt-2 leading-none">Welcome back.</h2>
              <p className="text-[#475569] text-sm mt-3">Sign in to continue to the fund administration console.</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         className="mt-1.5 bg-white border-[#e2e1dd]" data-testid="login-email-input" required />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                         className="mt-1.5 bg-white border-[#e2e1dd]" data-testid="login-password-input" required />
                </div>
                <Button type="submit" disabled={loading}
                        className="w-full bg-[#0b192c] hover:bg-[#0b192c]/90 text-white font-semibold h-11"
                        data-testid="login-submit-button">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="flex justify-end -mt-1">
                  <Link to="/forgot-password" className="text-[12px] font-semibold hover:underline"
                        style={{ color: "#475569" }} data-testid="login-forgot-link">
                    Forgot your password?
                  </Link>
                </div>
                <label className="flex items-center gap-2.5 text-[12px] text-[#475569] pt-1 cursor-pointer select-none"
                       data-testid="login-remember-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 accent-[#0b192c] border-[#e2e1dd] rounded cursor-pointer"
                    data-testid="login-remember-checkbox"
                  />
                  <span>
                    Keep me signed in on this device for 7 days.
                    <span className="block text-[11px] text-[#94a3b8]">
                      Leave unchecked on shared computers — you&apos;ll be signed out when you close the browser.
                    </span>
                  </span>
                </label>
              </form>

              <div className="mt-6 p-4 rounded-md border border-[#e2e1dd] bg-[#faf9f5] flex items-start gap-3" data-testid="login-admin-invite-hint">
                <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "#e8e6df" }}>
                  <span className="text-[10px] font-bold" style={{ color: "#0b192c" }}>i</span>
                </div>
                <p className="text-[12px] leading-relaxed text-[#475569]">
                  <span className="font-semibold text-[#0b192c]">Don&apos;t have an account?</span>
                  {" "}Your CorpZo administrator will create it and email your credentials.
                  For access, please contact <a href="mailto:reach@aifledger.com" className="text-[#0b192c] font-semibold hover:underline">reach@aifledger.com</a>.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] font-semibold">Two-factor required</div>
              <h2 className="font-serif text-4xl text-[#0b192c] mt-2 leading-none">Verify it&rsquo;s you{mfaName ? `, ${mfaName.split(" ")[0]}` : ""}.</h2>
              <p className="text-[#475569] text-sm mt-3">
                {useRecovery
                  ? "Enter one of your 8 backup recovery codes. Each code works once."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>

              <form onSubmit={onVerifyMfa} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="code" className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
                    <KeyRound size={12} /> {useRecovery ? "Recovery code" : "Authenticator code"}
                  </Label>
                  <Input
                    id="code"
                    inputMode={useRecovery ? "text" : "numeric"}
                    maxLength={useRecovery ? 20 : 6}
                    value={code}
                    onChange={(e) => setCode(useRecovery
                      ? e.target.value.toUpperCase()
                      : e.target.value.replace(/\D/g, ""))}
                    className="mt-1.5 bg-white border-[#e2e1dd] font-mono text-xl tracking-widest text-center h-12"
                    data-testid="login-mfa-code"
                    placeholder={useRecovery ? "ABCDE-12345" : "123 456"}
                    autoFocus
                  />
                </div>
                <Button type="submit" disabled={loading}
                        className="w-full bg-[#0b192c] hover:bg-[#0b192c]/90 text-white font-semibold h-11"
                        data-testid="login-mfa-submit">
                  {loading ? "Verifying…" : "Verify & sign in"}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" className="text-[#475569] hover:text-[#0b192c]"
                          onClick={() => { setMfaStage(false); setCode(""); setUseRecovery(false); }} data-testid="login-mfa-back">
                    ← Back to email &amp; password
                  </button>
                  <button type="button" className="text-[#475569] hover:text-[#0b192c] font-semibold"
                          onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
                          data-testid="login-mfa-toggle-recovery">
                    {useRecovery ? "Use authenticator code instead" : "Use a recovery code"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
